import { Editor, useEditor } from "@tiptap/react";
import { useEffect, useRef, useState } from "react";
import { UserResponse } from "stream-chat";
import { useChannelStateContext, useChatContext } from "stream-chat-react";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { searchChannelMentionUsers } from "util/searchChannelMentionUsers";
import { createChatComposerExtensions } from "./createChatComposerExtensions";
import { createEmojiSuggestion, EmojiPopup } from "./EmojiSuggestion";
import { stripParagraphIndent } from "./stripParagraphIndent";
import {
  createMentionSuggestion,
  MentionPopup,
} from "./createMentionSuggestion";

type UseChatComposerEditorArgs = {
  text: string;
  placeholder: string;
  isDisabled: boolean;
  isMentionDisabled: boolean;
  onTextChange: (markdown: string) => void;
  onSubmit: () => void;
  onSelectUser: (user: UserResponse) => void;
  onPasteFiles?: (files: File[]) => void;
};

export type ChatComposer = {
  editor: Editor;
  mentionPopup: MentionPopup | null;
  emojiPopup: EmojiPopup | null;
  isLinkInputOpen: boolean;
  openLinkInput: () => void;
  closeLinkInput: () => void;
};

export const COMPOSER_ELEMENT_ID = "message-textarea";
const COMPOSER_CLASS_NAME = "chat-composer";
const MARKDOWN_CONTENT_TYPE = "markdown";

/**
 * Rich text composer that reads and writes markdown, so the rest of the chat
 * (Stream's `text`, drafts, templates, AI inserts) keeps working with strings.
 */
export const useChatComposerEditor = (
  args: UseChatComposerEditorArgs
): ChatComposer => {
  const { text, placeholder, isDisabled } = args;
  const { channel } = useChannelStateContext();
  const { client } = useChatContext();
  const { isAdmin } = useRoleBasedView();

  const [mentionPopup, setMentionPopup] = useState<MentionPopup | null>(null);
  const [emojiPopup, setEmojiPopup] = useState<EmojiPopup | null>(null);
  const [isLinkInputOpen, setIsLinkInputOpen] = useState(false);

  // Tiptap extensions are created once, so they read callbacks through a ref
  const latestRef = useRef({
    ...args,
    channel,
    isAdmin,
    ownUserId: client.userID,
  });
  const lastEmittedTextRef = useRef(text);

  useEffect(() => {
    latestRef.current = { ...args, channel, isAdmin, ownUserId: client.userID };
  });

  const [extensions] = useState(() => {
    const mention = createMentionSuggestion({
      searchUsers: async (query) => {
        const { isMentionDisabled, channel, ownUserId, isAdmin } =
          latestRef.current;
        if (isMentionDisabled) return [];

        return searchChannelMentionUsers({
          channel,
          ownUserId,
          isAdmin,
          query,
        });
      },
      onSelectUser: (user) => latestRef.current.onSelectUser(user),
      onPopupChange: setMentionPopup,
    });
    const emoji = createEmojiSuggestion({ onPopupChange: setEmojiPopup });

    return createChatComposerExtensions({
      getPlaceholder: () => latestRef.current.placeholder,
      mentionSuggestion: mention.suggestion,
      emojiSuggestion: emoji.extension,
      isSuggestionListOpen: () => mention.isOpen() || emoji.isOpen(),
      onSubmit: () => latestRef.current.onSubmit(),
      onOpenLinkInput: () => setIsLinkInputOpen(true),
    });
  });

  const editor = useEditor({
    extensions,
    content: text,
    contentType: MARKDOWN_CONTENT_TYPE,
    editable: !isDisabled,
    editorProps: {
      attributes: {
        id: COMPOSER_ELEMENT_ID,
        class: COMPOSER_CLASS_NAME,
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": placeholder,
      },
      handlePaste: (_view, event) => {
        const { onPasteFiles } = latestRef.current;
        const files = Array.from(event.clipboardData?.files ?? []);
        if (!onPasteFiles || files.length === 0) return false;

        onPasteFiles(files);
        return true;
      },
    },
    onUpdate: ({ editor: updatedEditor }) => {
      const doc = stripParagraphIndent(updatedEditor.getJSON());
      // trimEnd drops the blank line from the trailing empty paragraph
      const markdown = (updatedEditor.markdown?.serialize(doc) ?? "").trimEnd();
      lastEmittedTextRef.current = markdown;
      latestRef.current.onTextChange(markdown);
    },
  });

  useEffect(() => {
    editor.setEditable(!isDisabled, false);
  }, [editor, isDisabled]);

  // Text set from outside the editor (draft load, templates, clear on send)
  useEffect(() => {
    if (text === lastEmittedTextRef.current) return;

    lastEmittedTextRef.current = text;
    editor.commands.setContent(text, {
      contentType: MARKDOWN_CONTENT_TYPE,
      emitUpdate: false,
    });
  }, [editor, text]);

  return {
    editor,
    mentionPopup,
    emojiPopup,
    isLinkInputOpen,
    openLinkInput: () => setIsLinkInputOpen(true),
    closeLinkInput: () => setIsLinkInputOpen(false),
  };
};
